import { format } from 'date-fns';
import { connect as connectSocket } from 'js/middleware/channels/socket';
import { rubricDocumentToOrgTemplate } from 'js/utils/rubric-org-scores';

const DATETIME_LOCAL_FMT = "yyyy-MM-dd'T'HH:mm";

export function datetimeLocalNow() {
  return format(new Date(), DATETIME_LOCAL_FMT);
}

export function datetimeLocalToUtcIso(localValue) {
  const d = new Date(String(localValue ?? '').trim());
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function optionalSnapshotName(value) {
  const name = typeof value === 'string' ? value.trim() : '';
  return name || null;
}

const SNAPSHOT_DATE_OPTS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

/** Date only at local midnight; otherwise date and time. */
export function formatSnapshotAsOf(value) {
  if (value == null || value === '') {
    return '';
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const hasTime =
    d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
  if (!hasTime) {
    return d.toLocaleDateString(undefined, SNAPSHOT_DATE_OPTS);
  }
  return d.toLocaleString(undefined, {
    ...SNAPSHOT_DATE_OPTS,
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function snapshotDisplayName(snapshot) {
  const name = optionalSnapshotName(snapshot?.name);
  if (name) {
    return name;
  }
  return formatSnapshotAsOf(snapshot?.snapshot_at) || 'Snapshot';
}

export function snapshotSetPath(workspaceId, orgId, sourceType, sourceKey) {
  return `/w/${workspaceId}/orgs/${orgId}/snapshots/${sourceType}/${encodeURIComponent(
    sourceKey
  )}`;
}

export function snapshotTrendsPath(
  workspaceId,
  orgId,
  sourceType,
  sourceKey,
  subOrgId = null
) {
  const base = `${snapshotSetPath(
    workspaceId,
    orgId,
    sourceType,
    sourceKey
  )}/trends`;
  return subOrgId != null && subOrgId !== '' ? `${base}/${subOrgId}` : base;
}

export function buildSnapshotTrendsPickerPath({
  workspaceId,
  orgId,
  sourceType,
  sourceKey,
  subOrgId,
  teamId,
}) {
  const pathname = snapshotTrendsPath(
    workspaceId,
    orgId,
    sourceType,
    sourceKey,
    subOrgId
  );
  const search =
    teamId != null && teamId !== ''
      ? `?teamId=${encodeURIComponent(teamId)}`
      : '';
  return { pathname, search };
}

export function isSnapshotTrendsPath(pathname) {
  if (pathname == null || pathname === '') {
    return false;
  }
  return /\/snapshots\/(?:assessment|rubric)\/[^/]+\/trends(?:\/|$)/.test(
    pathname
  );
}

export function snapshotCreatePath(workspaceId, orgId, sourceType, sourceKey) {
  return `${snapshotSetPath(workspaceId, orgId, sourceType, sourceKey)}/new`;
}

export function snapshotSourceKeysEqual(left, right) {
  if (left == null || right == null) {
    return false;
  }
  if (left === right) {
    return true;
  }
  try {
    return decodeURIComponent(left) === decodeURIComponent(right);
  } catch (_err) {
    return false;
  }
}

function usableSourceName(value, sourceKey) {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name || snapshotSourceKeysEqual(name, sourceKey)) {
    return null;
  }
  return name;
}

export function snapshotSourceName({
  sourceKey,
  snapshots = [],
  summaries = [],
  fallbackName = null,
} = {}) {
  const summaryName = summaries.find((row) =>
    snapshotSourceKeysEqual(row?.assessment_key, sourceKey)
  )?.assessment_name;
  const snapshotName = (snapshots ?? []).find((row) =>
    usableSourceName(row?.source_name, sourceKey)
  )?.source_name;

  return (
    usableSourceName(summaryName, sourceKey) ||
    usableSourceName(snapshotName, sourceKey) ||
    usableSourceName(fallbackName, sourceKey)
  );
}

export function assessmentTemplateFromCompareDocument({
  sourceKey,
  sourceName,
  document,
} = {}) {
  if (!document) {
    return null;
  }
  return rubricDocumentToOrgTemplate(
    sourceKey,
    sourceName || sourceKey,
    document
  );
}

export function snapshotReportPath(
  workspaceId,
  orgId,
  sourceType,
  sourceKey,
  snapshotId
) {
  return `${snapshotSetPath(
    workspaceId,
    orgId,
    sourceType,
    sourceKey
  )}/${snapshotId}`;
}

export function orgSnapshotTopic(nonce) {
  return `org_snapshot:create:${nonce}`;
}

export function randomNonce() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function liveAsOfHasScores(sourceType, data) {
  if (!data) {
    return false;
  }
  if (sourceType === 'rubric') {
    return (data.teams ?? []).some(
      (team) => team?.evaluation?.aggregate_score != null
    );
  }
  const rows = Array.isArray(data) ? data : data.teams;
  return (rows ?? []).some((team) =>
    (team.scores ?? []).some(
      (row) => row?.score != null && Number.isFinite(Number(row.score))
    )
  );
}

export function templateFromSnapshotPayload(snapshot) {
  const payload = snapshot?.payload ?? {};
  return rubricDocumentToOrgTemplate(
    payload.rubric_id || snapshot.source_key,
    payload.rubric_name || snapshot.source_name,
    payload.document || {},
    snapshot.snapshot_at ? new Date(snapshot.snapshot_at) : new Date()
  );
}

export function snapshotIdFromCreatePayload(payload) {
  const id = payload?.id ?? payload?.data?.id;
  return id == null || id === '' ? null : String(id);
}

export function applyOrgSnapshotCreateEvent(handlers, event, payload) {
  if (event === 'create_progress' && handlers.onProgress) {
    handlers.onProgress(payload);
    return;
  }
  if (event === 'create_complete' && handlers.onComplete) {
    handlers.onComplete(payload);
    return;
  }
  if (event === 'create_error' && handlers.onError) {
    handlers.onError(payload);
  }
}

export function joinOrgSnapshotCreateChannel(
  token,
  topic,
  handlers = {},
  joinPayload = {}
) {
  const socket = connectSocket(token);
  if (!socket) {
    return Promise.reject(new Error('Unable to connect'));
  }

  const channel = socket.channel(topic, joinPayload);
  let finished = false;
  const once = (fn) => (payload) => {
    if (!fn || finished) {
      return;
    }
    finished = true;
    try {
      fn(payload);
    } finally {
      channel.leave();
    }
  };

  const onComplete = once(handlers.onComplete);
  const onError = once(handlers.onError);

  if (handlers.onProgress) {
    channel.on('create_progress', handlers.onProgress);
  }
  channel.on('create_complete', onComplete);
  channel.on('create_error', onError);

  return new Promise((resolve, reject) => {
    channel
      .join()
      .receive('ok', (reply) => {
        applyOrgSnapshotCreateEvent(
          {
            onProgress: handlers.onProgress,
            onComplete,
            onError,
          },
          reply?.event,
          reply?.payload
        );
        resolve(channel);
      })
      .receive('error', (resp) => reject(resp))
      .receive('timeout', () => reject(new Error('Channel join timed out')));
  });
}
